import { useState, useEffect, useMemo } from "react";
import { useGetGalleryPhotosQuery } from "../features/api/galleryApiSlice";
import "./galleryPublic.scss";
import FuelLoading from "./FuelLoading";
import { HiArrowSmLeft, HiArrowSmRight } from "react-icons/hi";
import { FaTimes, FaGlobeAmericas, FaFolder } from "react-icons/fa";
import Section from "../components/ui/Section";
import PageHeader from "../components/PageHeader/PageHeader";

const REGIONS_LIST = [
  "All Regions",
  "Midwest",
  "Northeast",
  "South",
  "West",
  "Pacific Northwest",
];

export default function Gallery() {
  const { data: photos = [], isLoading, isError } = useGetGalleryPhotosQuery();

  // 🟢 TWO-TIER FILTER STATE ANCHORS
  const [selectedRegion, setSelectedRegion] = useState("All Regions");
  const [selectedState, setSelectedState] = useState("All States");

  // Lightbox Modal state parameters
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // 📊 COMPUTATION MATRIX 1: Dynamically compile a list of unique states active in the chosen region
  const availableStatesList = useMemo(() => {
    const states = new Set();
    photos.forEach((photo) => {
      if (
        selectedRegion === "All Regions" ||
        photo.regionGroup === selectedRegion
      ) {
        if (photo.stateName) states.add(photo.stateName);
      }
    });
    return ["All States", ...Array.from(states).sort()];
  }, [photos, selectedRegion]);

  // 📊 COMPUTATION MATRIX 2: Filter down images layout grid cleanly based on both conditions
  const filteredPhotos = useMemo(() => {
    return photos.filter((photo) => {
      const matchRegion =
        selectedRegion === "All Regions" ||
        photo.regionGroup === selectedRegion;
      const matchState =
        selectedState === "All States" || photo.stateName === selectedState;
      return matchRegion && matchState;
    });
  }, [photos, selectedRegion, selectedState]);

  // Handle resetting state filters when clicking between macro regions
  const handleRegionTabChange = (region) => {
    setSelectedRegion(region);
    setSelectedState("All States"); // Auto fallback reset
    setLightboxIndex(null);
  };

  const handleNextSlide = () => {
    setLightboxIndex((prev) => (prev + 1) % filteredPhotos.length);
  };

  const handlePrevSlide = () => {
    setLightboxIndex(
      (prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length
    );
  };

  // 🎹 KEYBOARD HOTKEYS: Allows cycling images using keyboard arrow keys
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") handleNextSlide();
      if (e.key === "ArrowLeft") handlePrevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, filteredPhotos]);

  if (isLoading) {
    return (
      <div className="gallery-public-loading">
        <FuelLoading text="Unrolling film logs..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="gallery-public-error">
        <p>⚠️ Unable to pull snapshot files down from cloud storage layers.</p>
      </div>
    );
  }

  const activePhoto =
    lightboxIndex !== null ? filteredPhotos[lightboxIndex] : null;

  return (
    <main className="gallery-public-page fuel-fade-in">
      <Section>
        <PageHeader
          title="Visual Archive"
          subtitle="A running catalog of landscapes, open spaces, and quiet detours—fixed in time across America."
        />

        {/* 🟢 TIER 1 NAVBAR: BROAD REGION FILTERS */}
        <nav className="gallery-filter-navbar macro-regions">
          {REGIONS_LIST.map((reg) => (
            <button
              key={reg}
              type="button"
              className={`filter-category-btn ${
                selectedRegion === reg ? "active" : ""
              }`}
              onClick={() => handleRegionTabChange(reg)}
            >
              {reg === "All Regions" ? (
                <>
                  <FaGlobeAmericas /> All Regions
                </>
              ) : (
                reg
              )}
            </button>
          ))}
        </nav>

        {/* 🟢 TIER 2 NAVBAR: SUB-LEVEL US STATE FOLDER CHIPS */}
        {availableStatesList.length > 1 && (
          <div className="state-folders-sub-shelf fuel-fade-in">
            <span className="folder-label-tag">
              <FaFolder /> State Folders:
            </span>
            <div className="folders-scroll-row">
              {availableStatesList.map((state) => (
                <button
                  key={state}
                  type="button"
                  className={`state-folder-chip ${
                    selectedState === state ? "selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedState(state);
                    setLightboxIndex(null);
                  }}
                >
                  {state === "All States" ? "📍 Show All" : `${state}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="gallery-public-container">
          {filteredPhotos.length === 0 ? (
            <p className="gallery-empty-text">
              No driving milestones captured inside this selection track yet.
              Stay tuned! 🗺️
            </p>
          ) : (
            /* MASONRY IMAGE FEED LIST GRID */
            <div className="gallery-masonry-grid">
              {filteredPhotos.map((photo, i) => (
                <button
                  key={photo._id || i}
                  type="button"
                  className="gallery-item-card"
                  onClick={() => setLightboxIndex(i)}
                >
                  <img src={photo.imageUrl} alt={photo.title} loading="lazy" />
                  <div className="gallery-item-card__overlay" />
                  <div className="gallery-item-card__info">
                    <h3>{photo.title}</h3>
                    <span>
                      📍 {photo.townName}, {photo.stateName}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ===== CINEMATIC LIGHTBOX MODAL OVERLAY WRAPPER ===== */}
        {activePhoto && (
          <div className="gallery-lightbox" role="dialog" aria-modal="true">
            {/* Blackout click backdrop container to exit */}
            <div
              className="gallery-lightbox__backdrop"
              onClick={() => setLightboxIndex(null)}
            />

            {/* Top Close Button Node Link */}
            <button
              type="button"
              className="lightbox-ctrl-btn close-btn"
              onClick={() => setLightboxIndex(null)}
            >
              <FaTimes />
            </button>

            {/* Left Arrow Navigation Trigger Button */}
            <button
              type="button"
              className="lightbox-ctrl-btn prev-btn"
              onClick={handlePrevSlide}
            >
              <HiArrowSmLeft />
            </button>

            {/* Full Screen Main Asset Box */}
            <div className="gallery-lightbox__content">
              <div className="image-stage-frame">
                <img
                  src={activePhoto.imageUrl}
                  alt={activePhoto.title}
                  className="lightbox-main-img"
                />
              </div>

              {/* Lower Caption Meta Panel Box */}
              <div className="lightbox-caption-box">
                <div className="caption-header">
                  <h2>{activePhoto.title}</h2>
                  <span className="caption-region-tag">
                    📍 {activePhoto.townName}, {activePhoto.stateName}
                  </span>
                </div>
                {activePhoto.caption && (
                  <p className="caption-desc">“ {activePhoto.caption} ”</p>
                )}
              </div>
            </div>

            {/* Right Arrow Navigation Trigger Button */}
            <button
              type="button"
              className="lightbox-ctrl-btn next-btn"
              onClick={handleNextSlide}
            >
              <HiArrowSmRight />
            </button>
          </div>
        )}
      </Section>
    </main>
  );
}
